import styled, { css } from 'styled-components'

// Neutrals and status colors come from the global CSS variables (src/style.js)
const TEXT = 'var(--color-text)'
const TEXT_SECONDARY = 'var(--color-muted)'
const HAIRLINE = 'var(--color-line)'

const STATUS_COLORS = {
  ok: 'var(--color-success)',
  warn: 'var(--color-warning)',
  error: 'var(--color-red)',
  none: 'var(--color-neutral)'
}

const fontSize = css`
  font-size: clamp(15px, 0.9vw, 17px);
`

export const Wrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px 32px 32px;
  background: ${({ theme }) => theme.colors.body};
  font-family: ${({ theme }) => theme.font};
  color: ${TEXT};
  overflow-y: auto;
`

export const Header = styled.header`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 24px;
  flex-shrink: 0;
`

export const BackButton = styled.button`
  justify-self: start;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 56px;
  padding: 0 24px 0 18px;
  border: 1px solid ${HAIRLINE};
  border-radius: 12px;
  background: #ffffff;
  color: ${({ theme }) => theme.colors.global};
  font-family: inherit;
  font-size: 17px;
  font-weight: 600;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: background 120ms ease, transform 120ms ease;

  svg {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
  }

  &:active {
    background: ${({ theme }) => theme.colors.body};
    transform: scale(0.98);
  }
`

export const Title = styled.h1`
  margin: 0;
  font-size: clamp(22px, 1.4vw, 28px);
  font-weight: 700;
  color: ${TEXT};
  text-align: center;
  white-space: nowrap;
`

export const Summary = styled.div`
  justify-self: end;
  display: flex;
  gap: 8px;
`

export const SummaryChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 16px;
  border: 1px solid ${HAIRLINE};
  border-radius: 999px;
  font-size: 16px;
  font-weight: 600;
  color: ${({ status }) => (status === 'warn' ? STATUS_COLORS.warn : TEXT)};
  background: ${({ status }) => (status === 'warn' ? 'var(--color-warning-soft)' : '#ffffff')};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`

export const Grid = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: start;
  align-content: start;
  gap: 24px;
`

export const Card = styled.section`
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid ${HAIRLINE};
  border-radius: var(--radius-card);
  overflow: hidden;
`

export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  border-bottom: 1px solid ${HAIRLINE};
`

export const CardIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.primary}1A;
  color: ${({ theme }) => theme.colors.primaryText || theme.colors.primary};

  svg {
    width: 22px;
    height: 22px;
  }
`

export const CardTitle = styled.h2`
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: clamp(17px, 1vw, 19px);
  font-weight: 700;
  color: ${TEXT};
`

export const CardBody = styled.div`
  padding: 4px 24px 8px;
`

export const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  min-height: clamp(44px, calc(6.4vh - 12px), 60px);
  border-bottom: 1px solid ${HAIRLINE};

  &:last-child {
    border-bottom: none;
  }
`

export const RowLabel = styled.span`
  ${fontSize}
  color: ${TEXT_SECONDARY};
`

export const RowValue = styled.span`
  ${fontSize}
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  text-align: right;
  color: ${({ status }) => (status === 'warn' || status === 'error' ? STATUS_COLORS[status] : TEXT)};
  font-variant-numeric: tabular-nums;
`

export const StatusDot = styled.span`
  display: inline-block;
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ status }) => STATUS_COLORS[status] || STATUS_COLORS.none};
`
